import React, { useState, useEffect } from 'react';
import { clsx } from 'clsx';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useUIStore, useRecallReportStore, useAvailabilityReportStore } from '@/stores';
import { useSettingsQueries } from '@/hooks/settings';
import { CounterpartyList } from '../components/CounterpartyList';
import { ReportSubTabs } from '../components/ReportSubTabs';
import { AvailabilityReportTab } from '../components/AvailabilityReportTab';
import { RecallReportTab } from '@/features/recall-report/pages/RecallReportPage';
import { ConfirmDialog } from '@/features/recall-report/components/ConfirmDialog';
import { TourSentinel } from '@/features/onboarding';

type PendingNavigation =
  | { type: 'tab'; tabId: string }
  | { type: 'counterparty'; cpName: string };

interface CounterpartyReportsTabProps {
  searchQuery?: string;
}

export const CounterpartyReportsTab: React.FC<CounterpartyReportsTabProps> = ({ searchQuery }) => {
  const { cp, reportTab } = useSearch({ strict: false }) as { cp?: string; reportTab?: string };
  const navigate = useNavigate();
  const setShowFilterSidebar = useUIStore((state) => state.setShowFilterSidebar);
  const setActiveCounterparty = useRecallReportStore((state) => state.setActiveCounterparty);
  const isEditing = useRecallReportStore((state) => state.isEditing);
  const hasChanges = useRecallReportStore((state) => state.hasChanges);
  const cancelEdit = useRecallReportStore((state) => state.cancelEdit);
  const isAvailEditing = useAvailabilityReportStore((state) => state.isEditing);
  const availHasChanges = useAvailabilityReportStore((state) => state.hasChanges);
  const cancelAvailEdit = useAvailabilityReportStore((state) => state.cancelEdit);

  const [pendingNavigation, setPendingNavigation] = useState<PendingNavigation | null>(null);
  const { data: settings } = useSettingsQueries();

  const selectedCp = cp || settings?.default_counterparty_id || 'velocity';
  const activeReportTab = reportTab || 'availability';

  // Ensure Zone C (right sidebar) appears on mount
  useEffect(() => {
    setShowFilterSidebar(true);
  }, [setShowFilterSidebar]);

  // Sync cp URL param to recall report store
  useEffect(() => {
    setActiveCounterparty(selectedCp);
  }, [selectedCp, setActiveCounterparty]);

  const guardNavigation = (action: PendingNavigation, executeAction: () => void) => {
    const anyEditing = isEditing || isAvailEditing;
    const anyChanges = (isEditing && hasChanges()) || (isAvailEditing && availHasChanges());
    if (anyEditing && anyChanges) {
      setPendingNavigation(action);
      return;
    }
    if (anyEditing) {
      cancelEdit();
      cancelAvailEdit();
    }
    executeAction();
  };

  const handleCounterpartySelect = (cpName: string) => {
    guardNavigation(
      { type: 'counterparty', cpName },
      () => navigate({
        to: '/inventory',
        search: { tab: 'counterparty-reports', cp: cpName, reportTab: activeReportTab, view_id: undefined },
        replace: true,
      })
    );
  };

  const handleReportTabChange = (tabId: string) => {
    guardNavigation(
      { type: 'tab', tabId },
      () => navigate({
        to: '/inventory',
        search: { tab: 'counterparty-reports', cp: selectedCp, reportTab: tabId, view_id: undefined },
        replace: true,
      })
    );
  };

  const handleConfirmNavigation = () => {
    cancelEdit();
    cancelAvailEdit();
    if (pendingNavigation?.type === 'tab') {
      navigate({
        to: '/inventory',
        search: { tab: 'counterparty-reports', cp: selectedCp, reportTab: pendingNavigation.tabId, view_id: undefined },
        replace: true,
      });
    } else if (pendingNavigation?.type === 'counterparty') {
      navigate({
        to: '/inventory',
        search: { tab: 'counterparty-reports', cp: pendingNavigation.cpName, reportTab: activeReportTab, view_id: undefined },
        replace: true,
      });
    }
    setPendingNavigation(null);
  };

  return (
    <div className="flex h-full">
      {/* Zone A: Left sidebar */}
      <div className="w-[260px] shrink-0 border-r border-gray-200 bg-white overflow-y-auto">
        <CounterpartyList selectedCp={selectedCp} onSelect={handleCounterpartySelect} />
      </div>
      {/* Zone B: Workspace */}
      <div className="relative flex-1 min-w-0 overflow-hidden flex flex-col bg-slate-50">
        <TourSentinel name="cpty-reports-overview" />
        <ReportSubTabs activeTab={activeReportTab} onTabChange={handleReportTabChange} />
        <div className={clsx(
          'flex-1 min-h-0 overflow-hidden',
          'bg-white border border-slate-200',
          'rounded-b-lg',
          activeReportTab === 'availability' ? 'rounded-tr-lg' : 'rounded-t-lg',
        )}>
          {activeReportTab === 'availability' ? (
            <AvailabilityReportTab counterparty={selectedCp} searchQuery={searchQuery} />
          ) : (
            <RecallReportTab searchQuery={searchQuery} />
          )}
        </div>
      </div>

      <ConfirmDialog
        show={pendingNavigation !== null}
        title="Unsaved Changes"
        message="You have unsaved changes. Navigating away will discard them. Continue?"
        confirmLabel="Discard Changes"
        cancelLabel="Stay"
        variant="danger"
        onConfirm={handleConfirmNavigation}
        onCancel={() => setPendingNavigation(null)}
      />
    </div>
  );
};
